import React, { FC, useCallback, useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import { useSelector } from "react-redux";

import DropZone from "../../elements/DropZone";
import HubSpotSelector from "../HubSpotSelector";
import classes from "./HubSpotDataUploader.module.scss";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { SelectOptionType } from "../../elements/Select/Select";
import { errorToaster } from "../../../helpers/toasters";
import { validateHubSpotData } from "../../../helpers/validateHubSpotData";

interface HubSpotDataUploaderProps {
  selectedBgAdvertiserId: string;
  setSelectedBgAdvertiserId: (id: string) => void;

  processValidatedData: (data: string[], fileName: string) => void;
  dataName: string;
  regex: RegExp;
  isStepInCampaignCreation: boolean;

  hubSpotSelectorOptions: SelectOptionType[];
  getDataFromHubSpot: () => void;
  handleOptionChange: (e: any) => void;
  hubSpotSelectorLabel: string;
  hubSpotSelectorButtonLabel: string;
  hubSpotSelectorValue: any;
  areDataFromHubSpotIsLoading: boolean;
  sampleLink: string;

  isChecked?: boolean;
  checkboxLabel?: string;
  onCheckboxChange?: (event: React.ChangeEvent<{}>, checked: boolean) => void;
}

const HubSpotDataUploader: FC<HubSpotDataUploaderProps> = ({
  selectedBgAdvertiserId,
  setSelectedBgAdvertiserId,
  processValidatedData,
  dataName,
  regex,
  isStepInCampaignCreation,
  hubSpotSelectorOptions,
  getDataFromHubSpot,
  handleOptionChange,
  hubSpotSelectorLabel,
  hubSpotSelectorButtonLabel,
  hubSpotSelectorValue,
  areDataFromHubSpotIsLoading,
  isChecked,
  checkboxLabel,
  onCheckboxChange,
  sampleLink,
}) => {
  const [didErrorOccurInUpload, setUploadError] = useState<boolean>(false);

  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const apiError = useSelector((state: ReduxApplicationState) => state.apiError);

  const isHubSpotDataPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);

  const validateData = useCallback(
    (dataRows: string[]) => validateHubSpotData(dataRows, regex, dataName),
    [dataName, regex],
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      setUploadError(false);
      acceptedFiles.forEach(async (file: any) => {
        try {
          const reader = new FileReader();
          reader.readAsText(file);
          reader.addEventListener("load", (event) => {
            const csv: string = event.target!.result as string;
            const rows = csv.split("\n").map((row) => row.replace(/\r/g, ""));
            const { validatedData, errorMessage } = validateData(rows);

            if (validatedData.length > 0) {
              processValidatedData(validatedData, file.name);
            }

            if (errorMessage) errorToaster(errorMessage, 10000);
          });
        } catch (error) {
          setUploadError(true);
          console.error("File upload error:", error);
        }
      });
    },
    [validateData, processValidatedData],
  );

  useEffect(() => {
    if (apiError.apiError) errorToaster(apiError.apiError, 5000);
  }, [apiError]);

  return (
    <div>
      {!isStepInCampaignCreation && (
        <div
          style={{
            margin: "3rem 0",
            width: "20rem",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="advertiser-select-label">Select Advertiser</InputLabel>
            <Select
              labelId="advertiser-select-label"
              id="advertiser-select"
              value={selectedBgAdvertiserId}
              onChange={(e) => setSelectedBgAdvertiserId(e.target.value as string)}
            >
              {advertisers.map((advertiser) => (
                <MenuItem key={advertiser.brandgenAdvertiserId} value={advertiser.brandgenAdvertiserId}>
                  {advertiser.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      {isHubSpotDataPending ? (
        <Typography className={classes.helperLabel}>Please, wait while we get everything in order...</Typography>
      ) : !isHubSpotDataPending && hubSpotSelectorOptions?.length ? (
        <HubSpotSelector
          label={`Select ${hubSpotSelectorLabel}`}
          value={hubSpotSelectorValue}
          options={hubSpotSelectorOptions}
          onButtonClick={getDataFromHubSpot}
          onChange={handleOptionChange}
          buttonLabel={hubSpotSelectorButtonLabel}
          isChecked={isChecked}
          checkboxLabel={checkboxLabel}
          onCheckboxChange={onCheckboxChange}
        />
      ) : (
        <Typography className={classes.helperLabel}>
          {selectedAdvertiser?.hubspot_authed
            ? `No ${hubSpotSelectorLabel} found linked to this advertiser's HubSpot Account.`
            : ""}
        </Typography>
      )}

      {!areDataFromHubSpotIsLoading && !isHubSpotDataPending && (
        <>
          {selectedAdvertiser?.hubspot_authed && (
            <div
              style={{
                width: "100%",
                height: "20px",
                borderBottom: "1px solid #C0C0C0",
                textAlign: "center",
                margin: "4rem 0",
              }}
            >
              <span
                style={{
                  fontSize: "25px",
                  backgroundColor: isStepInCampaignCreation ? "white" : "#fafafa",
                  padding: "0 10px",
                }}
              >
                OR
              </span>
            </div>
          )}

          <Typography variant="h2" className={!isStepInCampaignCreation ? classes.h2Title : ""}>
            Upload {dataName} via CSV
          </Typography>
          <DropZone
            explanatoryText={`Upload a CSV file with one column of ${dataName}. Each entry should be on a new line.`}
            sampleLink={sampleLink}
            onDrop={onDrop}
            didErrorOccurInUpload={didErrorOccurInUpload}
          />
        </>
      )}
    </div>
  );
};

export default HubSpotDataUploader;
